import type { ComputedRef, Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useConvertUsd } from '@/shared/composables/useConvertUsd'
import { paymentMethods } from '@/shared/constants/other.constants'
import { PurchaseFlowRoutes } from '@/shared/types/router.types'

export interface UseQuotePaymentDeps {
  quoteNumber: ComputedRef<string>
  getQuotePrice: ComputedRef<number>
  nonMatchingListLength: ComputedRef<number>
  isLoading: Ref<boolean>
  validateBeforePayment: () => Promise<boolean>
  onPurchaseStart: (payload: { paymentMethod: number; usdPrice: string | number }) => void
  loadEmit: (cb: () => Promise<void>, requestId?: string, withLoader?: boolean) => void
}

export const useQuotePayment = (deps: UseQuotePaymentDeps) => {
  const router = useRouter()

  const redirLink = (paymentMethod: number) => {
    window.location.href =
      paymentMethod === 0
        ? import.meta.env.VUE_APP_REDIRECT_CC
        : import.meta.env.VUE_APP_REDIRECT_DC
  }

  const pluggablePaySucceed = () =>
    router.replace({ name: PurchaseFlowRoutes.INSURANCE_RESULT })

  const onProceed = ({
    paymentMethod,
    isGpayOrApplePay,
  }: {
    paymentMethod: number
    isGpayOrApplePay: boolean
  }) => {
    deps.isLoading.value = true
    deps.loadEmit(
      async () => {
        try {
          if (!isGpayOrApplePay) {
            const isValid = await deps.validateBeforePayment()
            if (!isValid) return
          }

          const quoteNo = deps.quoteNumber.value
          const usdPrice = useConvertUsd(deps.getQuotePrice.value)

          localStorage.setItem('user-payload.quoteno', quoteNo)
          localStorage.setItem('user-payload.price', deps.getQuotePrice.value.toString())
          localStorage.setItem('paymentMethod', paymentMethods[paymentMethod])
          localStorage.setItem(
            'user-payload.uninsuredUsersCount',
            deps.nonMatchingListLength.value.toString()
          )
          localStorage.removeItem('user-payload.blockRemoveTravellers')

          deps.onPurchaseStart({ paymentMethod, usdPrice })

          if (isGpayOrApplePay) pluggablePaySucceed()
          else redirLink(paymentMethod)
        } finally {
          deps.isLoading.value = false
        }
      },
      'on-proceed-quote',
      false
    )
  }

  const getApplePayTotal = () => deps.getQuotePrice.value

  return { onProceed, getApplePayTotal }
}
